import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'

import decodeJWTToken from 'authorization-module/lib/helpers/decodeJWTToken'
import AuthCallback, {
  AuthResponseProps
} from 'authorization-module/lib/ui/atoms/Callback'
import config from 'const/config'
import routes from 'const/routes'
import authenticationRedirect from 'helpers/authenticationRedirect'
import getApigeeClientId from 'helpers/getApigeeClientId'
import getQueryParam from 'syf-js-utilities/formats/getQueryParam'
import { analyticsConfig } from 'syf-js-utilities/helpers/triggerAnalyticsEvent'
import {
  givenNameAtom,
  modalStatusAtom,
  selectedAccountAtom
} from 'store/atoms'
import { Modals } from 'store/atoms/typings'

interface AuthRouteConfig {
  openModal: Modals
  route: string
}

interface CallbackProps {
  setTokenFetched: Dispatch<SetStateAction<boolean>>
}

const Callback = ({ setTokenFetched }: CallbackProps): ReactElement => {
  const navigate = useNavigate()
  const [isRendered, setIsRendered] = useState(false)
  const errorParam = getQueryParam('error')
  const stateParam = getQueryParam('state')
  const previousRoute = sessionStorage.getItem(stateParam) || ''
  const updateGivenName = useSetAtom(givenNameAtom)
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const setSelectedAccount = useSetAtom(selectedAccountAtom)

  const authorizedCallback = () => {
    /*
     * OTP Error
     * http://localhost:3000/callback?error=user_declined&state=guaHUPaI&eror_description=user_something
     */
    if (previousRoute && errorParam) {
      sessionStorage.removeItem(stateParam)
      sessionStorage.setItem('otpErrorCallbackParams', window.location.search)

      // Generating a new token
      authenticationRedirect({
        navigateTo: previousRoute,
        scope: 'read write'
      })

      return
    }

    /*
     * Errors on callback URL
     * http://localhost:3000/callback?error=server_error&error_description=unexpected_error_occurred
     */
    if (errorParam) {
      navigate(routes.ERROR, { replace: true })
      return
    }

    navigate(routes.HOME, { replace: true })
  }

  const successCallback = (authResponse: AuthResponseProps) => {
    const { id_token: idToken } = authResponse
    const { givenName, subject } = decodeJWTToken(idToken)
    sessionStorage.setItem('idToken', idToken)
    sessionStorage.setItem('givenName', givenName)
    sessionStorage.setItem('subject', subject)
    const correlationId = sessionStorage.getItem('correlationId')
    analyticsConfig.defaults.Token = correlationId
    window?.newrelic?.addToTrace({ correlationId })
    window?.newrelic?.setUserId(correlationId)

    updateGivenName(givenName)
    setTokenFetched(true)
    // OTP Success or OTP new token generator after an error
    if (stateParam && previousRoute) {
      const routeConfig: AuthRouteConfig = JSON.parse(previousRoute)
      sessionStorage.removeItem(stateParam)
      const { openModal, route } = routeConfig
      if (openModal) {
        const selectedAccountId = sessionStorage.getItem('selectedAccountId')
        setSelectedAccount(selectedAccountId)
        updateModalStatus(openModal)
      }
      navigate(route, { replace: true })
      return
    }

    navigate(routes.HOME, { replace: true })
  }

  const errorCallback = () => {
    setTokenFetched(true)
    navigate(routes.ERROR, { replace: true })
  }

  const redirectUri = `${window.location.origin}${config.BASENAME}${routes.CALLBACK}`
  const client = localStorage.getItem('client') || getQueryParam('client')
  if (client) localStorage.setItem('client', client)
  const apigeeClientId = getApigeeClientId(client)

  useEffect(() => {
    setIsRendered(true)
  }, [])

  return (
    isRendered && (
      <AuthCallback
        authorizedCallback={authorizedCallback}
        successCallback={successCallback}
        reAttemptAuthorization={() => navigate(routes.HOME)}
        apigeeClientId={apigeeClientId}
        redirectUri={redirectUri}
        apiHost={config.API_HOST}
        errorCallback={errorCallback}
      />
    )
  )
}

export default Callback
